import { useInfiniteQuery } from "@tanstack/react-query";
import NewsContentGrid from "./components/NewsContentGrid";
import { ScreenLayout } from "../../components/layouts/components/ScreenLayout";
import { Box, Flex, Spinner } from "@chakra-ui/react";
import { getNextPageParam, getPreviousPageParam } from "../../utils/api";
import { useIsSmallBreakpoint } from "../../utils/useBreakpoints";
import { NewsCategoriesCarousel } from "./components/NewsCategoriesCarousel";
import { useAccountData } from "../../hooks/useAccountData";
import { getNews } from "./api/newsScreenApi";
import { useNewsCategories } from "./hooks/useNewsCategories";
import { BrowseEmptyState } from "../browse/components/EmptyState";
import { useEffect } from "react";
import { t } from "i18next";
import { useSearchParams } from "react-router-dom";
import { useTitleOnNavigation } from "../../hooks/useTitleOnNavigation";
import { useSubHeaderScroll } from "../../hooks/useSubHeaderScroll";

const GET_NEWS_QUERY_KEY = "GET_NEWS_QUERY_KEY";
export default function NewsScreen() {
  const accountData = useAccountData();
  const isSmallBreakpoint = useIsSmallBreakpoint();
  const { stickyTop: subHeaderTop, stickyTransform, stickyTransition } =
    useSubHeaderScroll();

  const [urlSearchParams] = useSearchParams();
  const tag = urlSearchParams.get("tag") ?? "";

  const { activeCategory } = useNewsCategories();

  const isVideoHub = activeCategory?.slug === "video-hub";

  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useInfiniteQuery({
      enabled: !!accountData,
      queryFn: (ctx) => {
        const pageParam = ctx.pageParam;
        return getNews({ categoryId: activeCategory?.id, pageParam, tag });
      },
      queryKey: [
        GET_NEWS_QUERY_KEY,
        activeCategory,
        accountData?.user?.id,
        tag,
      ],
      initialPageParam: 1,
      getNextPageParam,
      getPreviousPageParam,
    });

  // Scroll handler
  useEffect(() => {
    const container = window.document.documentElement;

    const handleScroll = () => {
      if (!container || isFetchingNextPage || !hasNextPage) return;

      const { scrollTop, scrollHeight, clientHeight } = container;
      const isBottom = scrollTop + clientHeight >= scrollHeight - 50;

      if (isBottom) {
        fetchNextPage();
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [hasNextPage, isFetchingNextPage]);

  useTitleOnNavigation(
    isVideoHub ? t("screens.hub.videos.title") : t("screens.hub.news.title"),
  );

  const pages = data?.pages;
  const articles = pages?.flatMap((p) => p.data.payload.results);

  if (!accountData) return null;

  return (
    <Flex gap={isSmallBreakpoint ? 0 : "30px"} direction="column" width="full">
      <Box
        paddingRight={isSmallBreakpoint ? 0 : "40px"}
        maxWidth="1200px"
        width="100%"
        alignSelf="center"
        top={subHeaderTop}
        transform={stickyTransform}
        transition={stickyTransition}
        position="sticky"
        bg="charcoal.charcoal"
        pb="10px"
        zIndex={1}
      >
        <ScreenLayout
          pageTitle={
            isVideoHub
              ? t("screens.hub.videos.title")
              : t("screens.hub.news.title")
          }
          hideSearch
        />
        {!isVideoHub && (
          <Flex
            justifyContent="space-between"
            pl={isSmallBreakpoint ? 4 : 0}
            pr={isSmallBreakpoint ? 4 : 6}
          >
            <NewsCategoriesCarousel />
          </Flex>
        )}
      </Box>
      {!data || isLoading || !articles?.length ? (
        <Flex
          width="100%"
          height="100%"
          justifyContent="center"
          alignItems="center"
        >
          {data && !isLoading ? (
            <BrowseEmptyState />
          ) : (
            <Spinner color="blaze.blaze" />
          )}
        </Flex>
      ) : (
        <NewsContentGrid
          articles={articles ?? []}
          isFetchingNextPage={isFetchingNextPage}
          isVideoHub={isVideoHub}
        />
      )}
    </Flex>
  );
}
